import { useState } from 'react'
import { Box, Button, Collapse, Divider, Group, Stack, Text, UnstyledButton } from '@mantine/core'
import { IconChevronUp, IconTrash } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { SectionCard } from '../layout/SectionCard'
import { FieldRenderer } from '../forms/FieldRenderer'
import { SlideList } from './SlideList'
import { getAtPath } from '../../utils/configPaths'
import classes from '../../styles/StudioEditorV2.module.css'

const wideTypes = new Set([
  'image',
  'image-editor',
  'focal-point',
  'rich-text',
  'textarea',
  'string-list',
  'contact-list',
])

// A slide field the schema marks `ui.advanced` moves out of the everyday form into the
// collapsed "Advanced options" block at the bottom of the slide.
const isAdvanced = (field) => Boolean(field.ui && field.ui.advanced)

export function SlideEditor({
  section,
  templateManifest,
  collection,
  configuration,
  errors,
  updateValue,
  addAsset,
  addRenditionPreview,
  assetUrls,
  assetSourceUrls,
  selectedSlide,
  setSelectedSlide,
  addSlide,
  removeSlide,
  moveSlide,
}) {
  const { t } = useTranslation()
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const collectionPath = collection?.path || 'slides'
  const slides = getAtPath(configuration, collectionPath, [])
  const slide = slides[selectedSlide]
  if (!collection || !slide) return null

  const itemSections = collection.itemSections || []
  const everyday = itemSections
    .map((itemSection) => ({ ...itemSection, fields: (itemSection.fields || []).filter((field) => !isAdvanced(field)) }))
    .filter((itemSection) => itemSection.fields.length > 0)
  const advanced = itemSections.flatMap((itemSection) => (itemSection.fields || []).filter(isAdvanced))

  const renderFields = (fields) => fields.map((field) => {
    const path = `${collectionPath}.${selectedSlide}.${field.path}`
    return (
      <Box
        key={field.path}
        className={field.ui?.width === 'full' || wideTypes.has(field.type) ? classes.fullField : undefined}
      >
        <FieldRenderer
          field={field}
          templateManifest={templateManifest}
          configuration={configuration}
          basePath={`${collectionPath}.${selectedSlide}`}
          item={slide}
          error={errors[path] || errors[`${collectionPath}.${selectedSlide}`]}
          onChange={updateValue}
          addAsset={addAsset}
          addRenditionPreview={addRenditionPreview}
          assetUrls={assetUrls}
          assetSourceUrls={assetSourceUrls}
        />
      </Box>
    )
  })

  return (
    <SectionCard
      title={section.label}
      description={t('editorV2.form.slides.description')}
    >
      <SlideList
        slides={slides}
        selected={selectedSlide}
        onSelect={setSelectedSlide}
        onAdd={addSlide}
        onMove={moveSlide}
        maxItems={collection.maxItems}
      />
      <Divider />
      <Stack gap="md">
        {everyday.map((itemSection) => (
          <Box key={itemSection.id}>
            <Text size="xs" fw={750} tt="uppercase" c="dimmed" mb="xs">
              {itemSection.label}
            </Text>
            <div className={classes.fieldGrid}>
              {renderFields(itemSection.fields)}
            </div>
          </Box>
        ))}

        {advanced.length > 0 && (
          <Box>
            <UnstyledButton
              className={classes.advancedToggle}
              onClick={() => setAdvancedOpen((value) => !value)}
              aria-expanded={advancedOpen}
            >
              <Text size="sm" fw={600}>{t('editorV2.form.slides.advancedOptions')}</Text>
              <IconChevronUp
                size={16}
                className={classes.cardChevron}
                data-open={advancedOpen ? 'true' : 'false'}
                aria-hidden="true"
              />
            </UnstyledButton>
            <Collapse in={advancedOpen}>
              <div className={classes.fieldGrid} style={{ marginTop: 14 }}>
                {renderFields(advanced)}
              </div>
            </Collapse>
          </Box>
        )}

        {/* Figma puts slide removal here as a destructive button rather than an icon
            hidden in the tab strip. */}
        <Group>
          <Button
            color="red"
            variant="filled"
            size="sm"
            radius="md"
            leftSection={<IconTrash size={16} />}
            disabled={slides.length <= (collection.minItems || 1)}
            onClick={() => removeSlide(selectedSlide)}
          >
            {t('editorV2.form.slides.deleteSlide')}
          </Button>
        </Group>
      </Stack>
    </SectionCard>
  )
}
